import { useQuery, UseQueryResult } from 'react-query';
import { useAggregatedQueries } from 'src/hooks/useAggregatedQuery';
import { AlarmHistory } from 'src/types/Alarms';
import { Organisation } from 'src/types/Organisation';
import { OrganisationInvite } from 'src/types/OrganisationInvite';
import { UserOrganisation } from 'src/types/UserOrganisation';
import { User } from 'src/types/User';
import { get, post } from '../utils';
import { getPage, Page, PageRequest, sortValueFrom } from '../pagination';

/** A membership with its user attached — see {@link getOrganisationMembersPage}. */
export interface UserOrganisationWithUser extends UserOrganisation {
  /** Absent when the membership outlives the user row. */
  user?: User;
}

enum UserOrganisationQueryKeys {
  UserOrganisationsByOrganisationId = 'UserOrganisationsByOrganisationId',
  UserOrganisationsByUserId = 'UserOrganisationsByUserId',
  UserOrganisations = 'UserOrganisation',
}

export function getUserOrganisationsByOrganisationId(organisationId: string): Promise<UserOrganisation[]> {
  return get<UserOrganisation[]>('userorganisation', { organisationId });
}

/** Sort fields the member list accepts. They are USER columns — see the API's list. */
export const ORGANISATION_MEMBER_SORT_FIELDS = ['email', 'firstName', 'lastName', 'userCreatedAt'] as const;

/**
 * How a row is ordered by each of {@link ORGANISATION_MEMBER_SORT_FIELDS}.
 *
 * `userCreatedAt` names WHICH createdAt it orders by — the row spreads the membership
 * first, so its top-level createdAt is the join date while the user's is the account date.
 */
export const getOrganisationMemberSortValue = sortValueFrom<UserOrganisationWithUser>({
  email: (member) => member.user?.email,
  firstName: (member) => member.user?.firstName,
  lastName: (member) => member.user?.lastName,
  userCreatedAt: (member) => member.user?.createdAt,
});

/**
 * ONE page of an organisation's members, with the total.
 *
 * `includeUser` removed one request per member; it did not bound the response. An
 * organisation with thousands of members still answered with all of them, and the members
 * table still counted and searched them in the browser. This asks for the rows it shows
 * and lets the database do the searching.
 */
export function getOrganisationMembersPage(
  organisationId: string,
  request: PageRequest
): Promise<Page<UserOrganisationWithUser>> {
  // `includeUser` is sent even though the PAGED path always attaches the user, because
  // the unpaged path does not. While ORG_SCALE_PAGINATION is off the server ignores the
  // paging parameters and answers with the full membership list — and without this that
  // list carries no users at all, so every row would render as a blank name and a blank
  // email rather than as a slow table.
  return getPage<UserOrganisationWithUser>(
    'userorganisation',
    request,
    { organisationId, includeUser: true },
    {
      // The legacy branch is `UserOrganisationModel.findAll({ where: { organisationId } })`
      // — it has never known `search` or `sort`. Without these the members search box would
      // answer with the organisation's first twenty members while the flag is off.
      // `id` too: the table this replaced was filtered across every rendered column, and
      // operators paste a uuid in to find one row. The server's SEARCHABLE_MEMBER_COLUMNS
      // carries it for the same reason.
      searchable: (member) => [
        member.userId,
        member.user?.id,
        member.user?.email,
        member.user?.firstName,
        member.user?.lastName,
      ],
      sortValue: getOrganisationMemberSortValue,
    }
  );
}

/** One page of an organisation's members, as a query. */
export function useOrganisationMembersPage(organisationId: string | undefined, request: PageRequest) {
  return useQuery<Page<UserOrganisationWithUser>, Error>(
    [UserOrganisationQueryKeys.UserOrganisationsByOrganisationId, 'page', organisationId, request],
    () => getOrganisationMembersPage(organisationId as string, request),
    {
      enabled: Boolean(organisationId),
      // The rows stay while the next page loads, so paging dims the table instead of
      // emptying it.
      keepPreviousData: true,
    }
  );
}

export function getUserOrganisationsByUserId(userId: string): Promise<UserOrganisation[]> {
  return get<UserOrganisation[]>('userorganisation', { userId });
}

export function getUserAlertHistory(userId: string): Promise<AlarmHistory[]> {
  return get<UserOrganisation[]>('alerthistory', { userId, limit: 100 });
}

export function addUserToOrganisation(userId: string, organisationId: string, userRoleId: number): Promise<UserOrganisation> {
  const body = {
    userId,
    organisationId,
    userRoleId,
  };
  return post<UserOrganisation>('userorganisation', body);
}

export function inviteUserToOrganisation(email: string, organisationId: string, userRoleId: number): Promise<OrganisationInvite> {
  const body = {
    email,
    organisationId,
    userRoleId,
  };
  return post<OrganisationInvite>('userorganisation/invite', body);
}

export function useQueryUserOrganisationsByUserId(userId: string): UseQueryResult<UserOrganisation[]> {
  return useQuery([UserOrganisationQueryKeys.UserOrganisationsByUserId, userId], () => getUserOrganisationsByUserId(userId));
}

export function useQueryUserOrganisationsByOrganisationId(organisationId: string): UseQueryResult<Organisation[]> {
  return useQuery([UserOrganisationQueryKeys.UserOrganisationsByOrganisationId, organisationId], () => getUserOrganisationsByOrganisationId(organisationId));
}

export function useQueryAllUserOrganisationsByOrganisationIds(
  ids: string[],
  includeDeleted?: boolean,
  enabled?: boolean
) {
  return useAggregatedQueries<UserOrganisation[]>(
    enabled !== undefined && !enabled
      ? []
      : ids.map((id) => {
          const organisationId = id;
          return {
            queryKey: [UserOrganisationQueryKeys.UserOrganisations, organisationId],
            queryFn: async () => {
              const device = await getUserOrganisationsByOrganisationId(organisationId);
              return device;
            },
          };
        })
  );
}

export function triggerSendAlarm(userId, item) {
  const body = {
    userId,
    type: 'AlarmHistory',
    item,
  };
  return post<null>('userAlert', body);
}

type ExpoPushMessage = {
  data?: object;
  title?: string;
  subtitle?: string;
  body?: string;
  sound?:
    | 'default'
    | null
    | {
        critical?: boolean;
        name?: 'default' | null;
        volume?: number;
      };
  ttl?: number;
  expiration?: number;
  priority?: 'default' | 'normal' | 'high';
  badge?: number;
  channelId?: string;
};

export function triggerSendPushMessage(userId, push: ExpoPushMessage) {
  const body = {
    userId,
    type: 'AlertMessage',
    item: {
      push,
    },
  };
  return post<null>('userAlert', body);
}

interface Email {
  subject: string;
  plainTextBody?: string;
  htmlBody?: string;
}

export function triggerSendEmail(userId, email: Email) {
  const body = {
    userId,
    type: 'AlertMessage',
    item: {
      email,
    },
  };
  return post<null>('userAlert', body);
}
